import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {selectIsSuperadmin, selectUserId} from '../store/features/Auth/authSlice.ts';
import {useUpdateConnectionStatusMutation} from '../store/features/Team/teamService.ts';
import {getAccessToken, subscribeToAccessTokenChanges} from '../services/authRuntime.ts';

export const CONNECTION_REPORT_INTERVAL_MS = 5 * 60 * 1000;

export const CONNECTION_REPORT_POLL_MS = 60 * 1000;
export const CONNECTION_REPORT_FAILURE_BACKOFF_MS = 60 * 1000;

export const createThrottledReporter = (
    send: () => Promise<unknown>,
    now: () => number = Date.now,
    intervalMs: number = CONNECTION_REPORT_INTERVAL_MS,
    backoffMs: number = CONNECTION_REPORT_FAILURE_BACKOFF_MS,
) => {
    let lastSentAt: number | null = null;
    let lastFailedAt: number | null = null;
    let inFlight = false;
    return () => {
        if (inFlight) {
            return;
        }
        const current = now();
        if (lastSentAt !== null && current - lastSentAt < intervalMs) {
            return;
        }
        if (lastFailedAt !== null && current - lastFailedAt < backoffMs) {
            return;
        }
        inFlight = true;
        send().then(
            () => {
                lastSentAt = current;
                lastFailedAt = null;
                inFlight = false;
            },
            () => {
                lastFailedAt = current;
                inFlight = false;
            },
        );
    };
};

const useAuthReady = (): boolean => {
    const [ready, setReady] = useState(() => Boolean(getAccessToken()));
    useEffect(() => {
        setReady(Boolean(getAccessToken()));
        return subscribeToAccessTokenChanges(() => setReady(Boolean(getAccessToken())));
    }, []);
    return ready;
};

export const useReportDesktopConnection = (): void => {
    const userId = useSelector(selectUserId);
    const isSuperadmin = useSelector(selectIsSuperadmin);
    const authReady = useAuthReady();
    const [updateConnectionStatus] = useUpdateConnectionStatusMutation();

    useEffect(() => {
        if (!userId || isSuperadmin || !authReady) {
            return;
        }
        const report = createThrottledReporter(() =>
            updateConnectionStatus({userId: String(userId), type: 'DESKTOP'}).unwrap(),
        );
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                report();
            }
        };
        report();
        window.addEventListener('focus', report);
        document.addEventListener('visibilitychange', onVisibility);
        const timer = window.setInterval(onVisibility, CONNECTION_REPORT_POLL_MS);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener('focus', report);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [userId, isSuperadmin, authReady, updateConnectionStatus]);
};
